import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { toJpeg } from "html-to-image";

// Asiakkaalle näytettävät lopulliset myyntihinnat.
// Hinnat sisältävät Painonetin meille laskuttaman ALV:n kustannusvaikutuksen
// sekä noin 30 % myyntikatteen.
const COASTER_PRICES = {
  single: {
    100: 315,
    250: 339,
    500: 355,
  },
  double: {
    100: 332,
    250: 359,
    500: 377,
  },
};

const COASTER_PRINT_OPTIONS = [
  { id: "single" },
  { id: "double" },
];

const STAND_PRICE = 305;
const STAND_SIZE = "600 × 2300 mm";

const DELIVERY_OPTIONS = [
  { id: "pickup", price: 0 },
  { id: "post_small", price: 18 },
  { id: "post_express", price: 27 },
  { id: "courier", price: 45 },
];

function formatEuro(value, locale = "fi-FI") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const PRODUCT_DISPLAY_FONT =
  '"Barlow Condensed", "Arial Narrow", Arial, sans-serif';

const STAND_PREVIEW = {
  scale: 1,
  x: 0,
  y: 0,
  maxHeight: "min(calc(100% - 1rem), 34rem)",
};

const STAND_PRINT_AREA = {
  left: 15.8,
  right: 20,
  top: 0,
  bottom: 9.5,
};

const PREVIEW_IMAGE_SOURCES = [
  "/brand/coaster-front-background.webp",
  "/brand/coaster-back-flame-ring.webp",
  "/brand/stand-background.png",
  "/brand/stand-frame.webp",
  "/brand/stand-preview-stage.webp",
  "/brand/paranoidi-logo-transparent.png",
  "/brand/painonet-logo.png",
  "/brand/buukkeri-logo.webp",
];

const PRODUCTS = [
  { id: "stand" },
  { id: "coaster" },
];

function clean(value) {
  return String(value || "").trim();
}

function visibleUrl(value) {
  return clean(value).replace(/^https?:\/\//i, "");
}

function nameFromSlug(slug, fallbackName) {
  const value = clean(slug)
    .replace(/^trubaduuri-/, "")
    .replace(/^bilebandi-/, "")
    .replace(/^duo-/, "")
    .replace(/-/g, " ");

  return value
    ? value.replace(/\b\w/g, (letter) => letter.toUpperCase())
    : fallbackName;
}

function twoLineName(value, fallbackName) {
  const words = clean(value).split(/\s+/).filter(Boolean);

  if (words.length <= 1) {
    return words[0] || fallbackName;
  }

  let bestSplit = 1;
  let smallestDifference = Infinity;

  for (let index = 1; index < words.length; index += 1) {
    const firstLine = words.slice(0, index).join(" ");
    const secondLine = words.slice(index).join(" ");
    const difference = Math.abs(firstLine.length - secondLine.length);

    if (difference < smallestDifference) {
      smallestDifference = difference;
      bestSplit = index;
    }
  }

  return `${words.slice(0, bestSplit).join(" ")}\n${words
    .slice(bestSplit)
    .join(" ")}`;
}

function ControlLabel({ children }) {
  return (
    <span className="mb-2 block text-xs font-black uppercase tracking-[0.14em] text-zinc-400">
      {children}
    </span>
  );
}

function ProductButton({ active, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-3xl border p-5 text-left transition ${
        active
          ? "border-red-400/55 bg-red-500/15 shadow-lg shadow-red-950/20"
          : "border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]"
      }`}
    >
      <span className="block text-lg font-black text-white">{title}</span>
      <span className="mt-2 block text-sm leading-6 text-zinc-400">
        {description}
      </span>
    </button>
  );
}

function SafeImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function ParanoidiLogo({ compact = false }) {
  return (
    <SafeImage
      src="/brand/paranoidi-logo-transparent.png"
      alt="Paranoidi"
      className={
        compact
          ? "h-7 w-auto max-w-[76%] object-contain"
          : "h-10 w-auto max-w-[80%] object-contain"
      }
    />
  );
}

function Partners({ compact = false, stacked = false }) {
  const { t } = useTranslation("admin");

  return (
    <div className="flex w-full flex-col items-center px-2">
      <span
        className={`font-black uppercase tracking-[0.14em] text-white/65 ${
        compact
          ? "text-[0.32rem] sm:text-[0.36rem] lg:text-[0.40rem]"
          : "text-[0.43rem]"
        }`}
      >
        {t("productCustomizer.artwork.collaboration")}
      </span>

        <div
          className={`mt-2 flex items-end justify-center ${
            stacked
              ? "flex-col gap-1.5"
              : compact
                ? "gap-3"
                : "gap-4"
          }`}
        >
        <SafeImage
          src="/brand/painonet-logo.png"
          alt="Painonet"
          className={
            stacked
              ? compact
            ? "h-auto w-[2.45rem] object-contain sm:w-[2.8rem] lg:w-[3.2rem]"
            : "h-auto w-[3.8rem] object-contain"
              : compact
                ? "max-h-4 max-w-[5.8rem] object-contain"
                : "max-h-5 max-w-[7rem] object-contain"
          }
        />

        <div className="flex translate-y-[1.2px] items-center justify-center gap-1.5">
          <SafeImage
            src="/brand/buukkeri-logo.webp"
            alt=""
            className={
              stacked
              ? compact
              ? "h-3 w-3 shrink-0 object-contain sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4"
              : "h-4 w-4 shrink-0 object-contain"
                : compact
                  ? "h-4 w-4 shrink-0 object-contain"
                  : "h-4 w-4 shrink-0 object-contain"
            }
          />

          <span
            className={`font-black uppercase tracking-[0.08em] text-white ${
              stacked
                ? compact
                ? "text-[0.31rem] sm:text-[0.36rem] lg:text-[0.42rem]"
                : "text-[0.42rem]"
                : compact
                  ? "text-[0.46rem]"
                  : "text-[0.42rem]"
            }`}
          >
            Buukkeri
          </span>
        </div>
      </div>
    </div>
  );
}

function ParanoidiStageBackground() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 72% 16%, rgba(239,68,68,0.42), transparent 20%), radial-gradient(circle at 38% 22%, rgba(239,68,68,0.16), transparent 18%), linear-gradient(180deg, #171315 0%, #0d0d0f 48%, #080809 100%)",
        }}
      />

      <div
        className="pointer-events-none absolute inset-x-[7%] top-[14%] h-[22%] opacity-35"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(239,68,68,0.22), transparent 66%)",
        }}
      />

      <div
        className="pointer-events-none absolute -left-[29%] top-[34%] h-[44%] w-[56%] opacity-42"
        style={{
          background:
            "repeating-radial-gradient(ellipse at center, transparent 0 8px, rgba(239,68,68,0.82) 9px 10px, transparent 11px 17px)",
        }}
      />

      <div
        className="pointer-events-none absolute -right-[29%] top-[34%] h-[44%] w-[56%] opacity-42"
        style={{
          background:
            "repeating-radial-gradient(ellipse at center, transparent 0 8px, rgba(239,68,68,0.82) 9px 10px, transparent 11px 17px)",
        }}
      />

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[15%] bg-gradient-to-t from-red-950/35 to-transparent"
      />

      <div
        className="pointer-events-none absolute inset-x-[-3%] bottom-0 h-[11%] bg-black/75"
        style={{
          clipPath:
            "polygon(0 70%,4% 58%,7% 70%,11% 48%,15% 68%,19% 55%,23% 72%,28% 46%,33% 70%,38% 52%,43% 72%,48% 44%,53% 70%,58% 50%,63% 72%,68% 48%,73% 70%,78% 54%,83% 72%,88% 47%,93% 69%,97% 56%,100% 70%,100% 100%,0 100%)",
        }}
      />
    </>
  );
}

function ArtistIdentity({
  performerName,
  logoDataUrl,
  logoName,
  logoScale,
  logoX,
  logoY,
  compact = false,
  distressed = false,
}) {
  const { t } = useTranslation("admin");
  const distressFilterId = useId().replace(/:/g, "");
  const displayName = twoLineName(
    performerName,
    t("productCustomizer.fallbackPerformer")
  );
  const longestLineLength = Math.max(
    ...displayName.split("\n").map((line) => line.length)
  );

  const nameSizeClass = compact
    ? longestLineLength <= 9
      ? "text-[clamp(1.15rem,18cqw,1.9rem)]"
      : longestLineLength <= 14
        ? "text-[clamp(0.95rem,15cqw,1.55rem)]"
        : "text-[clamp(0.72rem,12.5cqw,1.2rem)]"
    : longestLineLength <= 9
      ? "text-[clamp(1.8rem,15cqw,4.4rem)]"
      : longestLineLength <= 14
      ? "text-[clamp(1.3rem,11.5cqw,3.3rem)]"
      : "text-[clamp(1.05rem,9.5cqw,2.6rem)]";

  const compactNameScale = compact
    ? longestLineLength <= 9
      ? 1
      : longestLineLength <= 14
        ? 0.94
        : 0.86
    : 1;

  if (logoDataUrl) {
    return (
      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        style={{
          left: `${logoX}%`,
          top: `${logoY}%`,
          width: `${logoScale}%`,
          height: compact
            ? `${Math.max(10, logoScale / 2.35)}%`
            : `${logoScale}%`,
        }}
      >
        <img
          src={logoDataUrl}
          alt={logoName || performerName}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }

  return (
    <>
      {distressed && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-0"
        >
          <defs>
            <filter
              id={distressFilterId}
              x="-8%"
              y="-8%"
              width="116%"
              height="116%"
              colorInterpolationFilters="sRGB"
            >
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.72"
                numOctaves="3"
                seed="12"
                result="noise"
              />

              <feColorMatrix
                in="noise"
                type="matrix"
                values="
                  1 0 0 0 0
                  0 1 0 0 0
                  0 0 1 0 0
                  0 0 0 38 -32
                "
                result="speckles"
              />

              <feComposite
                in="SourceGraphic"
                in2="speckles"
                operator="out"
                result="distressedText"
              />

              <feDisplacementMap
                in="distressedText"
                in2="noise"
                scale="0.7"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>
      )}

      <div
        className="absolute inset-x-[9%] flex -translate-y-1/2 items-center justify-center"
        style={{ top: `${logoY}%` }}
      >
        <p
          className={`whitespace-pre-line text-center font-bold uppercase leading-[0.9] text-white drop-shadow-[0_4px_0_rgba(0,0,0,0.55)] ${nameSizeClass}`}
          style={{
            fontFamily: PRODUCT_DISPLAY_FONT,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            ...(distressed
              ? {
                  filter: `url(#${distressFilterId})`,
                }
              : {}),
            transform: `scaleX(${compactNameScale})`,
            transformOrigin: "center",
          }}
        >
          {displayName}
        </p>
      </div>
    </>
  );
}

function QrCode({ value }) {
  const { t } = useTranslation("admin");

  if (!value) {
    return (
      <div className="flex h-full w-full items-center justify-center text-center text-xs font-black uppercase tracking-[0.1em] text-zinc-500">
        {t("productCustomizer.qrMissing")}
      </div>
    );
  }

  return (
    <QRCodeSVG
      value={value}
      size={512}
      level="H"
      includeMargin={false}
      bgColor="#ffffff"
      fgColor="#000000"
      className="h-full w-full"
    />
  );
}

function ParanoidiSingleCoaster({
  performerName,
  logoDataUrl,
  logoName,
  logoScale,
  logoX,
  logoY,
  qrValue,

  // Vedos ei saa muuttua sen mukaan, tehdäänkö tilaus
  // puhelimella vai desktopilla. Proof käyttää aina
  // yksipuolisen tuotteen desktop-/painoasettelua.
  proofMode = false,
}) {
  const { t } = useTranslation("admin");

  /*
   * MOBIILI JA DESKTOP OVAT TÄSSÄ TARKOITUKSELLA ERILLÄÄN.
   *
   * Base-luokat = mobiili / tablet.
   * lg:-luokat   = desktop.
   *
   * proofMode ohittaa responsive-luokat kokonaan ja käyttää
   * aina vakioitua desktop-/painovedoksen asettelua.
   */

  const leftColumnClass = proofMode
    ? "absolute left-[15%] top-[29%] z-10 h-[42%] w-[28%]"
    : "absolute left-[17%] top-[28%] z-10 h-[42%] w-[28%] lg:left-[15%] lg:top-[27%]";

  const artistIdentityClass = proofMode
    ? "relative h-[43%] w-full scale-100"
    : "relative h-[43%] w-full scale-[0.58] lg:scale-100";

const ctaClass = proofMode
  ? "mt-[8%] text-center"
  : "mt-[4%] scale-[0.68] text-center lg:mt-[8%] lg:scale-100";

  const biisitoiveClass = proofMode
    ? "relative left-0 mt-1 text-[clamp(1.35rem,7.2cqw,2.4rem)] font-bold uppercase leading-[0.88] tracking-[-0.015em] text-red-500 drop-shadow-[0_2px_5px_rgba(0,0,0,1)]"
    : "relative -left-[5%] mt-1 text-[clamp(1.35rem,7.2cqw,2.4rem)] font-bold uppercase leading-[0.88] tracking-[-0.015em] text-red-500 drop-shadow-[0_2px_5px_rgba(0,0,0,1)] lg:left-0";

  const partnersClass = proofMode
    ? "absolute inset-x-[22%] bottom-[17%] z-10 scale-[0.92]"
    : "absolute inset-x-[22%] bottom-[17%] z-10 scale-[0.65] lg:scale-[0.92]";

  return (
    <div
      className="
        relative aspect-square
        shrink-0 overflow-hidden rounded-full
        border-[7px] border-black bg-black text-white
        shadow-[0_22px_55px_rgba(0,0,0,0.58)]
        sm:border-[9px]
        sm:shadow-[0_30px_80px_rgba(0,0,0,0.65)]
      "
      style={{
        width: "min(94cqw, 94cqh, 34rem)",
        containerType: "inline-size",
      }}
    >
      {/* Yksipuolisen oma tausta */}
      <img
        src="/brand/coaster-back-flame-ring.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        loading="eager"
        decoding="async"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
      />

      {/* Taustan rauhoitus */}
      <div className="pointer-events-none absolute inset-0 bg-black/42" />

      <div
        className="pointer-events-none absolute inset-[4%] rounded-full"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.12) 60%, rgba(0,0,0,0.02) 100%)",
        }}
      />

      <div className="pointer-events-none absolute inset-[3.5%] rounded-full border border-red-500/40" />

      {/* Paranoidi-logo */}
      <div className="absolute inset-x-0 top-[16%] z-10 flex justify-center">
        <SafeImage
          src="/brand/paranoidi-logo-transparent.png"
          alt="Paranoidi"
          className="h-auto w-[40%] max-w-[9rem] object-contain"
        />
      </div>

      {/* VASEN PUOLI */}
      <div className={leftColumnClass}>
        {/* Esiintyjän nimi / oma logo */}
        <div className={artistIdentityClass}>
          <ArtistIdentity
            performerName={performerName}
            logoDataUrl={logoDataUrl}
            logoName={logoName}
            logoScale={Math.min(logoScale, 72)}
            logoX={50}
            logoY={50}
            compact
            distressed
          />
        </div>

        {/* Erotusviiva */}
        <div className="mx-auto mt-[2%] h-px w-[72%] bg-red-500/85 lg:mt-[5%]" />

        {/* CTA */}
        <div
          className={ctaClass}
          style={{
            fontFamily: PRODUCT_DISPLAY_FONT,
            fontWeight: 700,
          }}
        >
          <p className="text-[clamp(0.75rem,3.8cqw,1.15rem)] font-bold leading-none text-white drop-shadow-[0_2px_5px_rgba(0,0,0,1)]">
            {t("productCustomizer.artwork.scanAndLeave")}
          </p>

          <p className={biisitoiveClass}>
            {t("productCustomizer.artwork.songRequest")}
          </p>
        </div>
      </div>

      {/* OIKEA PUOLI – QR
          QR:n sijainti on tällä hetkellä hyvä sekä mobiilissa että desktopissa,
          joten sitä ei sidota vasemman puolen responsive-säätöihin.
      */}
      <div className="absolute right-[18%] top-[31%] z-10 flex w-[30%] flex-col items-center">
        <div className="flex aspect-square w-full items-center justify-center rounded-[6px] bg-white p-[4%] shadow-[0_8px_24px_rgba(0,0,0,0.78)]">
          <QrCode value={qrValue} />
        </div>

        <p
          className="mt-2 w-[150%] whitespace-nowrap text-center font-bold leading-none text-white drop-shadow-[0_2px_4px_rgba(0,0,0,1)]"
          style={{
            fontFamily: PRODUCT_DISPLAY_FONT,
            fontSize: "clamp(0.42rem,2.35cqw,0.72rem)",
            letterSpacing: "-0.015em",
          }}
        >
          {visibleUrl(qrValue)}
        </p>
      </div>

      {/* Kumppanit
          Mobiilissa tarkoituksella pienemmät.
          Desktopilla palautetaan aiempi suurempi koko.
      */}
      <div className={partnersClass}>
        <Partners compact />
      </div>
    </div>
  );
}

function ParanoidiCoaster({
  side,
  performerName,
  logoDataUrl,
  logoName,
  logoScale,
  logoX,
  logoY,
  qrValue,
}) {
  const { t } = useTranslation("admin");
  const isFront = side === "front";

  return (
   <div
      className="
        relative aspect-square
        shrink-0 overflow-hidden rounded-full
        border-[7px] border-black bg-black text-white
        shadow-[0_22px_55px_rgba(0,0,0,0.58)]
        sm:border-[9px]
        sm:shadow-[0_30px_80px_rgba(0,0,0,0.65)]
      "
      style={{
        width: "min(94cqw, 94cqh, 34rem)",
        containerType: "inline-size",
      }}
    >
      {/* Molemmat taustat ladataan heti. Näkyvyys vain vaihdetaan. */}
      <img
        src="/brand/coaster-front-background.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        loading="eager"
        decoding="async"
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover object-center ${
          isFront ? "opacity-100" : "opacity-0"
        }`}
      />

      <img
        src="/brand/coaster-back-flame-ring.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        loading="eager"
        decoding="async"
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover object-center ${
          isFront ? "opacity-0" : "opacity-100"
        }`}
      />

      {isFront ? (
        <div className="pointer-events-none absolute inset-0 bg-black/10" />
      ) : (
        <>
          <div className="pointer-events-none absolute inset-0 bg-black/50" />

          <div
            className="pointer-events-none absolute inset-[5%] rounded-full"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.28) 58%, rgba(0,0,0,0.06) 100%)",
            }}
          />

          <div className="pointer-events-none absolute inset-[3.5%] rounded-full border border-red-500/45" />

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[26%] bg-gradient-to-t from-black via-black/80 to-transparent" />
        </>
      )}

      {isFront ? (
        <div className="absolute inset-0 z-10 translate-y-[3%]">
          {/* Paranoidi-logo */}
          <div className="absolute inset-x-0 top-[8%] flex justify-center sm:top-[10%]">
            <SafeImage
              src="/brand/paranoidi-logo-transparent.png"
              alt="Paranoidi"
              className="
                h-auto w-[35%] max-w-[10rem] object-contain
                sm:w-[32%] sm:max-w-[11rem]
              "
            />
          </div>

          {/* Esiintyjän nimi tai ladattu logo */}
          <div className="absolute inset-0 origin-center scale-[1.14] sm:scale-100">
            <ArtistIdentity
              performerName={performerName}
              logoDataUrl={logoDataUrl}
              logoName={logoName}
              logoScale={Math.min(logoScale, 68)}
              logoX={logoX}
              logoY={
                logoDataUrl
                  ? Math.min(Math.max(logoY, 25), 42)
                  : 35
              }
              distressed
            />
          </div>

          {/* Erotusviiva */}
          <div
            className="
              absolute inset-x-[21%] top-[55%] h-px bg-red-500/90
              sm:inset-x-[22%] sm:top-[54%]
            "
          />

          {/* Toimintateksti */}
          <div
            className="
              absolute inset-x-[6%] top-[59%] text-center
              sm:inset-x-[7%] sm:top-[58%]
            "
            style={{
              fontFamily: PRODUCT_DISPLAY_FONT,
              fontWeight: 700,
            }}
          >
            <p
              className="
                text-[clamp(1rem,5.5cqw,1.55rem)]
                font-bold uppercase leading-none text-white
                drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]
                sm:text-[clamp(1rem,5cqw,1.5rem)]
              "
            >
              {t("productCustomizer.artwork.leave")}
            </p>

            <p
              className="
                mt-1
                text-[clamp(1.6rem,9cqw,2.75rem)]
                font-bold uppercase leading-[0.9]
                tracking-[-0.01em] text-red-500
                drop-shadow-[0_2px_5px_rgba(0,0,0,0.9)]
                sm:mt-1.5
                sm:text-[clamp(1.65rem,8.2cqw,2.65rem)]
              "
            >
              {t("productCustomizer.artwork.songRequest")}
            </p>
          </div>
        </div>
      ) : (
        <>
       {/* Kääntöpuolen otsikko */}
        <div
          className="
            absolute inset-x-[6%] top-[10%] z-10 text-center
            sm:inset-x-[7%] sm:top-[11%]
          "
          style={{
            fontFamily: PRODUCT_DISPLAY_FONT,
            fontWeight: 700,
          }}
        >
          <p
            className="
              text-[clamp(0.80rem,4.8cqw,1.4rem)]
              font-bold leading-none text-white
              drop-shadow-[0_2px_5px_rgba(0,0,0,1)]
              sm:text-[clamp(0.95rem,4cqw,1.35rem)]
            "
          >
            {t("productCustomizer.artwork.scanAndLeave")}
          </p>

          <p
            className="
              mt-1.2
              text-[clamp(1.2rem,6.6cqw,2.3rem)]
              font-bold uppercase leading-none
              tracking-[-0.01em] text-red-500
              drop-shadow-[0_2px_5px_rgba(0,0,0,1)]
              sm:text-[clamp(2.0rem,6.2cqw,2.7rem)]
            "
          >
            {t("productCustomizer.artwork.songRequest")}
          </p>
        </div>

          {/* QR-koodi ja osoite */}
          <div
            className="
              absolute left-1/2 top-[25%] z-10
              flex w-[39%] -translate-x-1/2 flex-col items-center
              sm:top-[25%] sm:w-[37%]
            "
          >
            <div className="flex aspect-square w-full items-center justify-center rounded-[5px] bg-white p-[4%] shadow-[0_6px_20px_rgba(0,0,0,0.75)]">
              <QrCode value={qrValue} />
            </div>

            <p
              className="
                mt-2.5 w-[220%] whitespace-nowrap
                text-center font-bold leading-none text-white
                drop-shadow-[0_2px_4px_rgba(0,0,0,1)]
                sm:mt-4 sm:w-[230%]
              "
              style={{
                fontFamily: PRODUCT_DISPLAY_FONT,
                fontSize: "clamp(0.48rem, 2.9cqw, 0.82rem)",
                letterSpacing: "-0.015em",
              }}
            >
              {visibleUrl(qrValue)}
            </p>
          </div>

          {/* Kumppanit */}
          <div
            className="
              absolute inset-x-[8%] bottom-[14%] z-10
              sm:inset-x-[15%] sm:bottom-[16%]
            "
          >
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <div className="h-px w-[11%] shrink-0 bg-red-500/70 sm:w-[12%]" />

              <span
                className="
                  shrink-0 text-[0.4rem] font-black uppercase
                  tracking-[0.1em] text-white/75
                  sm:text-[0.52rem] sm:tracking-[0.13em]
                "
              >
                {t("productCustomizer.artwork.collaboration")}
              </span>

              <div className="h-px w-[11%] shrink-0 bg-red-500/70 sm:w-[12%]" />
            </div>

            <div className="mt-1.5 flex items-center justify-center gap-3 sm:mt-2.5 sm:gap-4">
              <SafeImage
                src="/brand/painonet-logo.png"
                alt="Painonet"
                className="h-auto w-[3.1rem] object-contain sm:w-[4.4rem]"
              />

              <div className="flex translate-y-[1px] items-center justify-center gap-1 sm:min-w-[4.4rem]">
                <SafeImage
                  src="/brand/buukkeri-logo.webp"
                  alt=""
                  className="
                    h-[0.85rem] w-[0.85rem] shrink-0 object-contain
                    sm:h-[1.1rem] sm:w-[1.1rem]
                  "
                />

                <span
                  className="
                    text-[0.38rem] font-black uppercase
                    tracking-[0.04em] text-white
                    sm:text-[0.52rem] sm:tracking-[0.05em]
                  "
                >
                  Buukkeri
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ParanoidiStand({
  performerName,
  logoDataUrl,
  logoName,
  logoScale,
  logoX,
  logoY,
  qrValue,
}) {
  const { t } = useTranslation("admin");

  return (
    <div className="relative z-10 flex h-full w-full min-h-0 flex-1 items-center justify-center">
      {/*
        Tämä näkymätön kuva antaa wrapperille stand-frame.png-tiedoston
        oikean luonnollisen leveyden. Kuvaa ei enää venytetä väärään
        aspect ratioon.
      */}
      <div
        className="relative max-h-full scale-[1.12] translate-y-[1%] sm:scale-100 sm:translate-y-0"
        style={{
          height: STAND_PREVIEW.maxHeight,
          transformOrigin: "center center",
        }}
      >
        <img
          src="/brand/stand-frame.webp"
          alt=""
          aria-hidden="true"
          className="block h-full w-auto opacity-0"
        />

        {/* Varjo lattialle */}
        <div className="pointer-events-none absolute bottom-[1.5%] left-1/2 h-[3%] w-[62%] -translate-x-1/2 rounded-full bg-black/25 blur-lg" />

        {/*
          Painettava kangaspinta.

          Nämä arvot perustuvat lähettämääsi frame-kuvaan.
          Painatus on nyt kapeampi ja hieman vasemmalla verrattuna
          edelliseen toteutukseen.
        */}
        <div
          className="absolute z-10 overflow-hidden bg-black text-white shadow-[0_20px_50px_rgba(0,0,0,0.30)]"
            style={{
              left: `${STAND_PRINT_AREA.left}%`,
              right: `${STAND_PRINT_AREA.right}%`,
              top: `${STAND_PRINT_AREA.top}%`,
              bottom: `${STAND_PRINT_AREA.bottom}%`,
              borderRadius: "15% 15% 0 0 / 4% 4% 0 0",
              containerType: "inline-size",
            }}
        >
          {/* Ständin kiinteä tausta */}
          <img
            src="/brand/stand-background.png"
            alt=""
            aria-hidden="true"
            draggable="false"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          />

          {/* Kevyt sivuvarjostus */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-black/20" />

          {/* Alaosan tummennus logoille */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[24%] bg-gradient-to-t from-black via-black/85 to-transparent" />

          {/* Paranoidi-logo */}
          <div className="absolute inset-x-0 top-[4%] z-10 flex justify-center px-[8%]">
            <ParanoidiLogo compact />
          </div>

          {/* Esiintyjän nimi tai oma logo */}
          <ArtistIdentity
            performerName={performerName}
            logoDataUrl={logoDataUrl}
            logoName={logoName}
            logoScale={Math.min(logoScale, 66)}
            logoX={logoX}
            logoY={logoDataUrl ? Math.min(logoY, 28) : 27}
            compact
            distressed
          />

          {/* Erotusviiva */}
          <div className="absolute inset-x-[22%] top-[38.5%] z-10 h-px bg-red-500/85" />

          {/* Toimintateksti */}
          <p
            className="absolute left-1/2 top-[42.5%] z-10 w-[150%] whitespace-nowrap text-center font-bold leading-none text-white"
            style={{
              transform: "translateX(-50%) scaleX(0.7)",
              transformOrigin: "center",
              fontFamily: PRODUCT_DISPLAY_FONT,
              fontWeight: 700,
              fontSize: "clamp(0.34rem, 4.8cqw, 0.61rem)",
            }}
          >
            {t("productCustomizer.artwork.scanAndLeaveSongRequest")}
          </p>

          {/* QR-koodi */}
          <div className="absolute left-1/2 top-[49%] z-10 flex w-[60%] -translate-x-1/2 flex-col items-center">
            <div className="flex aspect-square w-full items-center justify-center bg-white p-[4.5%] shadow-[0_4px_14px_rgba(0,0,0,0.5)]">
              <QrCode value={qrValue} />
            </div>

            <p
              className="mt-3 w-full whitespace-nowrap text-center font-bold leading-none text-white/90"
              style={{
                fontFamily: PRODUCT_DISPLAY_FONT,
                fontSize: "clamp(0.25rem, 4.4cqw, 0.55rem)",
                letterSpacing: "-0.02em",
              }}
            >
              {visibleUrl(qrValue)}
            </p>
          </div>

          {/* Kumppanit */}
          <div className="absolute inset-x-[4%] bottom-[8%] z-10 flex justify-center sm:bottom-[6.5%] lg:bottom-[5%]">
            <Partners compact stacked />
          </div>
        </div>

        {/* Fyysinen ständi painatuksen päälle */}
        <img
          src="/brand/stand-frame.webp"
          alt=""
          aria-hidden="true"
          draggable="false"
          className="pointer-events-none absolute inset-0 z-30 h-full w-full object-contain"
        />
      </div>
    </div>
  );
}


function StandPreviewBackground() {
  return (
    <>
      <style>{`
        @keyframes paranoidiStandStageDrift {
          0% {
            transform: scale(1.04) translate3d(-0.35%, -0.25%, 0);
          }

          50% {
            transform: scale(1.07) translate3d(0.4%, 0.2%, 0);
          }

          100% {
            transform: scale(1.05) translate3d(-0.1%, 0.35%, 0);
          }
        }

        .paranoidi-stand-stage-motion {
          animation: paranoidiStandStageDrift 18s ease-in-out infinite alternate;
          will-change: transform;
        }

        @media (prefers-reduced-motion: reduce) {
          .paranoidi-stand-stage-motion {
            animation: none;
            transform: scale(1.04);
          }
        }
      `}</style>

      <div
        aria-hidden="true"
        className="paranoidi-stand-stage-motion pointer-events-none absolute -inset-[3%] z-0"
        style={{
          backgroundImage: "url('/brand/stand-preview-stage.webp')",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      />
    </>
  );
}

function Preview({
  productType,
  coasterPrintMode,
  coasterSide,
  setCoasterSide,
  performerName,
  logoDataUrl,
  logoName,
  logoScale,
  logoX,
  logoY,
  qrValue,
}) {
  const { t } = useTranslation("admin");

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-red-300">
            {t("productCustomizer.preview.live")}
          </p>

          <h2 className="mt-2 text-xl font-black text-white sm:text-2xl">
            {productType === "coaster"
              ? t("productCustomizer.preview.coasterTitle")
              : t("productCustomizer.preview.standTitle", { size: STAND_SIZE })}
          </h2>
        </div>

        {productType === "coaster" && coasterPrintMode === "double" && (
          <div className="flex rounded-full border border-white/10 bg-black/40 p-1">
            {[
              ["front", t("productCustomizer.preview.front")],
              ["back", t("productCustomizer.preview.back")],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setCoasterSide(value)}
                className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.1em] transition ${
                  coasterSide === value
                    ? "bg-red-500 text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className={`relative isolate mt-4 flex flex-none items-center justify-center overflow-hidden rounded-[1.5rem] border border-white/10 sm:rounded-[2rem] ${
          productType === "stand"
            ? "h-[22rem] p-2 sm:h-[28rem] sm:p-3 lg:h-[27rem]"
            : "h-[18rem] p-2 sm:h-[28rem] sm:p-5 lg:h-[31rem]"
        }`}
        style={
          productType === "stand"
            ? {
                containerType: "size",
                background: "#070707",
              }
            : {
                containerType: "size",
                background:
                  "radial-gradient(circle at top, rgba(239,68,68,0.11), transparent 48%)",
              }
        }
      >
        {productType === "stand" && <StandPreviewBackground />}

        {productType === "coaster" ? (
          coasterPrintMode === "single" ? (
            <ParanoidiSingleCoaster
              performerName={performerName}
              logoDataUrl={logoDataUrl}
              logoName={logoName}
              logoScale={logoScale}
              logoX={logoX}
              logoY={logoY}
              qrValue={qrValue}
            />
          ) : (
            <ParanoidiCoaster
              side={coasterSide}
              performerName={performerName}
              logoDataUrl={logoDataUrl}
              logoName={logoName}
              logoScale={logoScale}
              logoX={logoX}
              logoY={logoY}
              qrValue={qrValue}
            />
          )
        ) : (
          <ParanoidiStand
            performerName={performerName}
            logoDataUrl={logoDataUrl}
            logoName={logoName}
            logoScale={logoScale}
            logoX={logoX}
            logoY={logoY}
            qrValue={qrValue}
          />
        )}
      </div>
    </div>
  );
}

export default function ProductCustomizer({ isLoggedIn, performer }) {
  const { t, i18n } = useTranslation("admin");
  const logoInputRef = useRef(null);

  const adminLocale = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en-GB"
    : "fi-FI";

  const coasterPrintOptions = COASTER_PRINT_OPTIONS.map((option) => ({
    ...option,
    label: t(`productCustomizer.coasterPrintOptions.${option.id}.label`),
    description: t(
      `productCustomizer.coasterPrintOptions.${option.id}.description`
    ),
  }));

  const deliveryOptions = DELIVERY_OPTIONS.map((option) => ({
    ...option,
    label: t(`productCustomizer.deliveryOptions.${option.id}.label`),
    description: t(`productCustomizer.deliveryOptions.${option.id}.description`),
  }));

  const products = PRODUCTS.map((product) => ({
    ...product,
    label: t(`productCustomizer.products.${product.id}.label`),
    description: t(`productCustomizer.products.${product.id}.description`),
  }));
  // Näistä näkymättömistä esikatseluista muodostetaan sähköpostiin
  // ja tilaukseen tallennettavat vedoskuvat.
  const standProofRef = useRef(null);
  const coasterFrontProofRef = useRef(null);
  const coasterBackProofRef = useRef(null);
  const [productType, setProductType] = useState("stand");
  const [coasterPrintMode, setCoasterPrintMode] = useState("single");
  const [coasterSide, setCoasterSide] = useState("front");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [logoName, setLogoName] = useState("");
  const [logoError, setLogoError] = useState("");
  const [logoScale, setLogoScale] = useState(58);
  const [logoX, setLogoX] = useState(50);
  const [logoY, setLogoY] = useState(31);
  const [coasterAmount, setCoasterAmount] = useState(100);
  const [standQuantity, setStandQuantity] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [savedMessage, setSavedMessage] = useState("");
  const [draftError, setDraftError] = useState("");
  const [isPreparingProof, setIsPreparingProof] = useState(false);

  useEffect(() => {
    const preloadedImages = PREVIEW_IMAGE_SOURCES.map((src) => {
      const image = new Image();
      image.decoding = "async";
      image.src = src;
      return image;
    });

    return () => {
      preloadedImages.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, []);

  const performerSlug = clean(performer?.slug);
  const performerName =
    clean(performer?.name) ||
    nameFromSlug(performerSlug, t("productCustomizer.fallbackPerformer"));
  const paranoidiUrl = performerSlug
    ? `https://paranoidi.fi/p/${performerSlug}`
    : "";
  const needsLogin = !isLoggedIn || !performerSlug;

  const selectedDelivery =
    deliveryOptions.find((option) => option.id === deliveryMethod) ||
    deliveryOptions[0];

  const selectedCoasterPrintLabel = t(
    `productCustomizer.coasterPrintOptions.${coasterPrintMode}.label`
  );

  const selectedPrice =
    productType === "coaster"
      ? COASTER_PRICES[coasterPrintMode][Number(coasterAmount)]
      : STAND_PRICE * standQuantity;

  // Toimitushinta on nyt tiedossa jo editorissa.
  const selectedDeliveryPrice = Number(selectedDelivery.price) || 0;

  // Asiakkaalle voidaan näyttää suoraan koko tilauksen lopullinen hinta.
  const selectedTotalPrice = selectedPrice + selectedDeliveryPrice;

  const selectedDescription =
    productType === "coaster"
      ? t("productCustomizer.summary.coasters", {
          count: coasterAmount,
          printMode: selectedCoasterPrintLabel,
        })
      : standQuantity === 1
        ? t("productCustomizer.summary.standSingle", { size: STAND_SIZE })
        : t("productCustomizer.summary.standMultiple", {
            count: standQuantity,
            size: STAND_SIZE,
          });

  const deliverySummary = t("productCustomizer.summary.deliverySummary", {
    label: selectedDelivery.label,
    price: formatEuro(selectedDeliveryPrice, adminLocale),
  });

  useEffect(() => {
    function handlePaste(event) {
      const imageItem = Array.from(event.clipboardData?.items || []).find(
        (item) => item.type.startsWith("image/")
      );

      if (!imageItem) {
        return;
      }

      const file = imageItem.getAsFile();

      if (file) {
        readLogo(file);
      }
    }

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [i18n.resolvedLanguage]);

  async function readLogo(file) {
    setLogoError("");
    setSavedMessage("");

    if (!file) {
      return;
    }

    const allowedTypes = ["image/png", "image/svg+xml"];

    if (!allowedTypes.includes(file.type)) {
      setLogoError(
        t("productCustomizer.messages.logoType")
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t("productCustomizer.messages.logoTooLarge"));
      return;
    }

    if (file.type === "image/png") {
      let bitmap;

      try {
        bitmap = await createImageBitmap(file);

        const longestSide = Math.max(bitmap.width, bitmap.height);

        if (longestSide < 2000) {
          setLogoError(
            t("productCustomizer.messages.pngTooSmall", {
              width: bitmap.width,
              height: bitmap.height,
            })
          );
          bitmap.close?.();
          return;
        }

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", {
          willReadFrequently: true,
        });

        canvas.width = 64;
        canvas.height = 64;

        if (!context) {
          bitmap.close?.();
          setLogoError(t("productCustomizer.messages.pngCheckFailed"));
          return;
        }

        context.drawImage(bitmap, 0, 0, 64, 64);

        const pixels = context.getImageData(0, 0, 64, 64).data;
        let hasTransparency = false;

        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 250) {
            hasTransparency = true;
            break;
          }
        }

        bitmap.close?.();

        if (!hasTransparency) {
          setLogoError(
            t("productCustomizer.messages.pngNoTransparency")
          );
          return;
        }
      } catch {
        bitmap?.close?.();
        setLogoError(t("productCustomizer.messages.pngCheckFailed"));
        return;
      }
    }

    const reader = new FileReader();

    reader.onload = () => {
      setLogoDataUrl(String(reader.result || ""));
      setLogoName(file.name || t("productCustomizer.uploadedLogo"));
    };

    reader.onerror = () => {
      setLogoError(t("productCustomizer.messages.logoReadFailed"));
    };

    reader.readAsDataURL(file);
  }

  function removeLogo() {
    setLogoDataUrl("");
    setLogoName("");
    setLogoError("");

    setLogoScale(58);
    setLogoX(50);
    setLogoY(31);

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  }

  function handleLogoDrop(event) {
    event.preventDefault();
    readLogo(event.dataTransfer.files?.[0]);
  }

  // Odotetaan, että vedoksen kuvat ja fontit ovat oikeasti latautuneet
  // ennen kuin DOM muutetaan kuvatiedostoksi.
  async function waitForProofAssets(node) {
    if (!node) {
      throw new Error(t("productCustomizer.messages.proofPreviewMissing"));
    }

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const images = Array.from(node.querySelectorAll("img"));

    await Promise.all(
      images.map((image) => {
        if (image.complete) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          const finish = () => resolve();

          image.addEventListener("load", finish, { once: true });
          image.addEventListener("error", finish, { once: true });
        });
      })
    );
  }

  // Vedos on sähköpostia ja tilausten tarkistamista varten.
  // 720 px JPEG pitää kuvan riittävän tarkkana mutta tiedostokoon maltillisena.
  async function createProofDataUrl(node) {
    await waitForProofAssets(node);

    return toJpeg(node, {
      quality: 0.95,
      pixelRatio: 1,
      backgroundColor: "#070707",
      cacheBust: false,
    });
  }

  async function saveDraft() {
    if (isPreparingProof) {
      return false;
    }

    setIsPreparingProof(true);
    setSavedMessage("");
    setDraftError("");

    try {
      let standPreviewDataUrl = "";
      let coasterFrontPreviewDataUrl = "";
      let coasterBackPreviewDataUrl = "";

      if (productType === "stand") {
        standPreviewDataUrl = await createProofDataUrl(
          standProofRef.current
        );
     } else {
        coasterFrontPreviewDataUrl = await createProofDataUrl(
          coasterFrontProofRef.current
        );

        // Yksipuolisessa tuotteessa ei ole toista hyväksyttävää vedosta.
        if (coasterPrintMode === "double") {
          coasterBackPreviewDataUrl = await createProofDataUrl(
            coasterBackProofRef.current
          );
        }
      }

      const draft = {
        productType,
        performerName,
        performerSlug,
        qrValue: paranoidiUrl,
        logoName,
        logoDataUrl,
        logoScale,
        logoX,
        logoY,
        coasterAmount,
        coasterPrintMode,
        coasterPrintLabel: selectedCoasterPrintLabel,
        standQuantity,
        standSize: STAND_SIZE,
        deliveryMethod: selectedDelivery.id,
        deliveryLabel: selectedDelivery.label,
        deliveryPrice: selectedDeliveryPrice,
        productPrice: selectedPrice,
        estimatedPrice: selectedTotalPrice,

        // Nämä kentät ContactPage lähettää jo sellaisenaan backendille.
        standPreviewDataUrl,
        coasterFrontPreviewDataUrl,
        coasterBackPreviewDataUrl,

        createdAt: new Date().toISOString(),
      };

      window.sessionStorage.setItem(
        "paranoidi_print_order_draft",
        JSON.stringify(draft)
      );

      setSavedMessage(t("productCustomizer.messages.draftSaved"));

      return true;
    } catch (error) {
      console.error("Painotuotteen vedoksen muodostaminen epäonnistui:", error);

      setDraftError(
        t("productCustomizer.messages.proofFailed")
      );

      return false;
    } finally {
      setIsPreparingProof(false);
    }
  }

  async function continueToQuote() {
    const saved = await saveDraft();

    if (!saved) {
      return;
    }

    window.location.href = "/yhteys?aihe=painotuote";
  }

  const previewProps = {
    productType,
    coasterPrintMode,
    coasterSide,
    setCoasterSide,
    performerName,
    logoDataUrl,
    logoName,
    logoScale,
    logoX,
    logoY,
    qrValue: paranoidiUrl,
  };

return (
  <div
    id="paranoidi-product-editor"
    className="product-customizer"
  >
        {/*
      VEDOSTEN RENDERÖINTI
      Näitä elementtejä ei näytetä käyttäjälle. Ne pidetään selaimen
      ulkopuolella, jotta html-to-image voi muodostaa niistä kuvat.
      display:none ei käy, koska silloin elementillä ei olisi renderöityä kokoa.
    */}
    <div
      aria-hidden="true"
      className="pointer-events-none fixed"
      style={{
        left: "-10000px",
        top: 0,
        width: "720px",
        zIndex: -1,
      }}
    >
      {productType === "stand" ? (
        <div
          ref={standProofRef}
          className="relative isolate flex h-[720px] w-[720px] items-center justify-center overflow-hidden"
          style={{
            containerType: "size",
            background: "#070707",
          }}
        >
          <StandPreviewBackground />

          <ParanoidiStand
            performerName={performerName}
            logoDataUrl={logoDataUrl}
            logoName={logoName}
            logoScale={logoScale}
            logoX={logoX}
            logoY={logoY}
            qrValue={paranoidiUrl}
          />
        </div>
      ) : (
        <>
          <div
            ref={coasterFrontProofRef}
            className="relative isolate flex h-[720px] w-[720px] items-center justify-center overflow-hidden p-6"
            style={{
              containerType: "size",
              background:
                "radial-gradient(circle at top, rgba(239,68,68,0.11), transparent 48%), #070707",
            }}
          >
            {coasterPrintMode === "single" ? (
            <ParanoidiSingleCoaster
              performerName={performerName}
              logoDataUrl={logoDataUrl}
              logoName={logoName}
              logoScale={logoScale}
              logoX={logoX}
              logoY={logoY}
              qrValue={paranoidiUrl}
              proofMode
            />
            ) : (
              <ParanoidiCoaster
                side="front"
                performerName={performerName}
                logoDataUrl={logoDataUrl}
                logoName={logoName}
                logoScale={logoScale}
                logoX={logoX}
                logoY={logoY}
                qrValue={paranoidiUrl}
              />
            )}
          </div>

          {coasterPrintMode === "double" && (
            <div
              ref={coasterBackProofRef}
              className="relative isolate mt-8 flex h-[720px] w-[720px] items-center justify-center overflow-hidden p-6"
              style={{
                containerType: "size",
                background:
                  "radial-gradient(circle at top, rgba(239,68,68,0.11), transparent 48%), #070707",
              }}
            >
              <ParanoidiCoaster
                side="back"
                performerName={performerName}
                logoDataUrl={logoDataUrl}
                logoName={logoName}
                logoScale={logoScale}
                logoX={logoX}
                logoY={logoY}
                qrValue={paranoidiUrl}
              />
            </div>
          )}
        </>
      )}
    </div>

    {/* Mobiilin toimintopalkki: seuraa mukana vain editorin sisällä */}
    <div
      className="pointer-events-none sticky z-[70] h-0 lg:hidden"
      style={{
        top: "calc(100dvh - 0.75rem)",
      }}
    >
      <div className="product-customizer-mobile-bar pointer-events-auto absolute inset-x-0 bottom-0 rounded-2xl border border-white/15 bg-zinc-950/95 p-3 shadow-2xl shadow-black/60 backdrop-blur">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[0.7rem] font-bold text-zinc-400">
              {selectedDescription}
            </p>
            <p className="text-xl font-black leading-none text-white">
              {formatEuro(selectedTotalPrice, adminLocale)}
            </p>
          </div>

          <p className="max-w-[10rem] text-right text-[0.62rem] leading-4 text-zinc-500">
            {deliverySummary}
          </p>
        </div>

        <div className="grid grid-cols-[0.8fr_1.2fr] gap-2">
          <button
            type="button"
            onClick={saveDraft}
            disabled={needsLogin || isPreparingProof}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/15 px-3 text-[0.68rem] font-black uppercase tracking-[0.08em] text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPreparingProof
              ? t("productCustomizer.buttons.preparing")
              : t("productCustomizer.buttons.save")}
          </button>

          <button
            type="button"
            onClick={continueToQuote}
            disabled={needsLogin || isPreparingProof}
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-red-500 px-3 text-[0.68rem] font-black uppercase tracking-[0.08em] text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPreparingProof
              ? t("productCustomizer.buttons.preparing")
              : t("productCustomizer.buttons.continueOrder")}
          </button>
        </div>

        {savedMessage && (
          <p className="mt-2 text-center text-xs font-bold text-emerald-300">
            {savedMessage}
          </p>
        )}

        {draftError && (
          <p className="mt-2 text-center text-xs font-bold text-red-300">
            {draftError}
          </p>
        )}
      </div>
    </div>

    <div className="grid items-start gap-5 pb-36 lg:grid-cols-[minmax(0,0.82fr)_minmax(30rem,1.18fr)] lg:gap-8 lg:pb-0">

        <section className="product-customizer-controls order-2 rounded-[1.5rem] border border-white/10 bg-zinc-950/90 p-4 shadow-2xl shadow-black/30 sm:rounded-[2rem] sm:p-7 lg:order-1">
          <div>
            <ControlLabel>{t("productCustomizer.controls.step1")}</ControlLabel>

            <div className="grid gap-3 sm:grid-cols-2">
              {products.map((product) => (
                <ProductButton
                  key={product.id}
                  active={productType === product.id}
                  title={product.label}
                  description={product.description}
                  onClick={() => setProductType(product.id)}
                />
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-white/10 pt-8">
            <ControlLabel>{t("productCustomizer.controls.step2")}</ControlLabel>

            <div className="rounded-3xl border border-red-400/40 bg-red-500/10 p-5">
              <span className="block font-black text-white">
                {t("productCustomizer.templateLocked.title")}
              </span>

              <span className="mt-2 block text-sm leading-6 text-zinc-400">
                {t("productCustomizer.templateLocked.description")}
              </span>
            </div>

            {needsLogin ? (
              <div className="mt-4 rounded-2xl border border-amber-300/25 bg-amber-400/10 p-4">
                <p className="text-sm font-bold leading-6 text-amber-100">
                  {t("productCustomizer.loginRequired")}
                </p>

                <a
                  href="/login"
                  className="mt-3 inline-flex rounded-full bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-black"
                >
                  {t("productCustomizer.buttons.login")}
                </a>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-4">
                <p className="text-xs font-black uppercase tracking-[0.12em] text-emerald-200">
                  {t("productCustomizer.loadedFromPerformer")}
                </p>

                <p className="mt-2 text-lg font-black text-white">
                  {performerName}
                </p>

                <p className="mt-1 break-all text-sm font-bold text-white/75">
                  {paranoidiUrl}
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 border-t border-white/10 pt-8">
            <ControlLabel>{t("productCustomizer.controls.step3")}</ControlLabel>

            <label
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleLogoDrop}
              className="flex cursor-pointer flex-col items-stretch gap-4 rounded-2xl border border-dashed border-white/20 bg-black/30 px-4 py-5 transition hover:border-red-400/40 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="min-w-0">
                <span className="block truncate font-black text-white">
                  {logoName || t("productCustomizer.logo.selectOrDrop")}
                </span>

               <span className="mt-1 block text-xs leading-5 text-zinc-500">
                {t("productCustomizer.logo.requirements")}
              </span>
              </span>

              <span className="flex w-full shrink-0 justify-center rounded-full bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.1em] text-black sm:w-auto sm:py-2">
                {t("productCustomizer.buttons.select")}
              </span>

              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.svg,image/png,image/svg+xml"
                onChange={(event) => readLogo(event.target.files?.[0])}
                className="sr-only"
              />
            </label>

            {logoDataUrl && (
              <>
              <button
                type="button"
                onClick={removeLogo}
                className="mt-4 inline-flex rounded-full border border-red-400/40 bg-red-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.1em] text-red-200 transition hover:bg-red-500/20 hover:text-white"
              >
                {t("productCustomizer.buttons.removeLogo")}
              </button>

                <div className="mt-6 grid gap-5">
                  {[
                    [
                      t("productCustomizer.logo.scale"),
                      logoScale,
                      setLogoScale,
                      28,
                      78,
                    ],
                    [
                      t("productCustomizer.logo.horizontal"),
                      logoX,
                      setLogoX,
                      25,
                      75,
                    ],
                    [
                      t("productCustomizer.logo.vertical"),
                      logoY,
                      setLogoY,
                      20,
                      40,
                    ],
                  ].map(([label, value, setter, min, max]) => (
                    <label key={label}>
                      <span className="flex items-center justify-between text-sm font-bold text-zinc-300">
                        <span>{label}</span>
                        <span className="text-zinc-500">{value}%</span>
                      </span>

                      <input
                        type="range"
                        min={min}
                        max={max}
                        value={value}
                        onChange={(event) =>
                          setter(Number(event.target.value))
                        }
                        className="mt-3 w-full accent-red-500"
                      />
                    </label>
                  ))}
                </div>
              </>
            )}

            {logoError && (
              <p className="mt-3 text-sm font-bold text-red-300">{logoError}</p>
            )}
          </div>

          <div className="mt-8 border-t border-white/10 pt-8">
            <ControlLabel>
              {productType === "coaster"
                ? t("productCustomizer.controls.step4Coaster")
                : t("productCustomizer.controls.step4Stand")}
            </ControlLabel>

            {productType === "coaster" ? (
            <div>
              <div className="grid gap-3 sm:grid-cols-2">
                {coasterPrintOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setCoasterPrintMode(option.id);

                      // Yksipuolisessa ei ole erillistä kääntöpuolta.
                      if (option.id === "single") {
                        setCoasterSide("front");
                      }
                    }}
                    className={`min-h-[6.5rem] rounded-2xl border px-4 py-4 text-left transition ${
                      coasterPrintMode === option.id
                        ? "border-red-400/55 bg-red-500/15"
                        : "border-white/10 bg-black/30 hover:border-white/20"
                    }`}
                  >
                    <span className="block text-sm font-black leading-5 text-white">
                      {option.label}
                    </span>

                    <span className="mt-2 block text-xs font-bold leading-5 text-zinc-400">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>

                <div className="mt-5 grid grid-cols-1 gap-3 min-[360px]:grid-cols-3">
                  {[100, 250, 500].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => setCoasterAmount(amount)}
                      className={`rounded-2xl border px-3 py-4 text-sm font-black transition ${
                        coasterAmount === amount
                          ? "border-red-400/55 bg-red-500/15 text-white"
                          : "border-white/10 bg-black/30 text-zinc-400"
                      }`}
                    >
                      <span className="block">
                        {t("productCustomizer.pieces", { count: amount })}
                      </span>

                      <span className="mt-1 block text-sm font-black text-white">
                        {formatEuro(
                          COASTER_PRICES[coasterPrintMode][amount],
                          adminLocale
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
            <div>
              <div className="rounded-2xl border border-red-400/55 bg-red-500/15 px-4 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-black text-white">
                      {STAND_SIZE}
                    </p>

                    <p className="mt-1 text-xs font-bold text-zinc-400">
                      {t("productCustomizer.stand.description")}
                    </p>

                    <p className="mt-2 text-xs font-bold text-zinc-500">
                      {t("productCustomizer.stand.pricePerPiece", {
                        price: formatEuro(STAND_PRICE, adminLocale),
                      })}
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <span className="block text-base font-black text-white">
                      {formatEuro(
                        STAND_PRICE * standQuantity,
                        adminLocale
                      )}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="text-sm font-black text-white">
                    {t("productCustomizer.stand.quantity")}
                  </span>

                  <div className="flex items-center rounded-full border border-white/15 bg-black/30 p-1">
                    <button
                      type="button"
                      onClick={() =>
                        setStandQuantity((quantity) =>
                          Math.max(1, quantity - 1)
                        )
                      }
                      disabled={standQuantity <= 1}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-lg font-black text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={t("productCustomizer.stand.decreaseQuantity")}
                    >
                      −
                    </button>

                    <span className="min-w-10 text-center text-sm font-black text-white">
                      {standQuantity}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setStandQuantity((quantity) =>
                          Math.min(20, quantity + 1)
                        )
                      }
                      disabled={standQuantity >= 20}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-lg font-black text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={t("productCustomizer.stand.increaseQuantity")}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <p className="mt-3 text-xs leading-5 text-zinc-500">
                {t("productCustomizer.stand.widerQuote")}
              </p>
            </div>
          )}
          </div>

          <div className="mt-8 border-t border-white/10 pt-8">
            <ControlLabel>{t("productCustomizer.controls.step5")}</ControlLabel>

            <div className="grid gap-3 sm:grid-cols-2">
              {deliveryOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setDeliveryMethod(option.id)}
                  className={`flex min-h-[8rem] flex-col rounded-2xl border px-4 py-4 text-left transition ${
                    deliveryMethod === option.id
                      ? "border-red-400/55 bg-red-500/15"
                      : "border-white/10 bg-black/30 hover:border-white/20"
                  }`}
                >
                  <span className="block text-sm font-black leading-5 text-white">
                    {option.label}
                  </span>

                  <span className="mt-1 block text-xs font-bold leading-5 text-zinc-400">
                    {option.description}
                  </span>

                  <span className="mt-auto pt-3 text-sm font-black text-white">
                    {formatEuro(option.price, adminLocale)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <aside className="product-customizer-preview order-1 min-w-0 lg:order-2 lg:sticky lg:top-4">
          <div className="flex min-w-0 flex-col rounded-[1.5rem] border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-4 shadow-2xl shadow-black/40 sm:rounded-[2rem] sm:p-5 lg:p-6">
            <Preview {...previewProps} />

            <div className="mt-5 hidden shrink-0 rounded-3xl border border-white/10 bg-black/45 p-5 lg:block">
              <div className="flex items-end justify-between gap-4">
                <div>
                 <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                    {t("productCustomizer.total.title")}
                  </p>

                  <p className="mt-2 text-3xl font-black text-white">
                    {formatEuro(selectedTotalPrice, adminLocale)}
                  </p>

                  <p className="mt-2 text-sm font-bold text-zinc-400">
                    {selectedDescription}
                  </p>

                </div>

                <p className="max-w-[16rem] text-right text-xs leading-5 text-zinc-500">
                  {t("productCustomizer.total.product", {
                    price: formatEuro(selectedPrice, adminLocale),
                  })}
                  <br />
                  {t("productCustomizer.total.delivery", {
                    price: formatEuro(selectedDeliveryPrice, adminLocale),
                  })}
                </p>
              </div>

            <div className="mt-5 grid grid-cols-[0.9fr_1.1fr] gap-3">
            <button
              type="button"
              onClick={saveDraft}
              disabled={needsLogin || isPreparingProof}
              className="
                inline-flex min-h-12 w-full items-center justify-center
                whitespace-nowrap rounded-full border border-white/15
                px-4 py-3 text-[0.7rem] font-black uppercase
                tracking-[0.08em] text-white transition
                hover:border-white/30 hover:bg-white/10
                disabled:cursor-not-allowed disabled:opacity-40
              "
            >
              {isPreparingProof
                ? t("productCustomizer.buttons.preparing")
                : t("productCustomizer.buttons.saveDraft")}
            </button>

            <button
              type="button"
              onClick={continueToQuote}
              disabled={needsLogin || isPreparingProof}
              className="
                inline-flex min-h-12 w-full items-center justify-center
                whitespace-nowrap rounded-full bg-red-500
                px-4 py-3 text-[0.7rem] font-black uppercase
                tracking-[0.08em] text-white transition
                hover:bg-red-400
                disabled:cursor-not-allowed disabled:opacity-40
              "
            >
              {isPreparingProof
              ? t("productCustomizer.buttons.preparing")
              : t("productCustomizer.buttons.continueOrder")}
            </button>
          </div>

              {savedMessage && (
                <p className="mt-3 text-center text-sm font-bold text-emerald-300">
                  {savedMessage}
                </p>
              )}

              {draftError && (
                <p className="mt-3 text-center text-sm font-bold text-red-300">
                  {draftError}
                </p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}